import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'src/components/queryProvider';
import { queryKeys, requestCCApi, ScheduleDetailsParams } from 'src/utils';

// invalidate current sidebar query after any operations
export const invalidateCalendarSidebarData = (
  currentSidebarDetailsDataParams: ScheduleDetailsParams | undefined,
  currentPosition: number,
  locationGroupId?: string,
  isOwnerOperator?: boolean,
) => {
  if (currentSidebarDetailsDataParams) {
    // clear cache of scheduleDetails endpoint in calendar sidebar
    queryClient.invalidateQueries(
      queryKeys.getCalendarSidebarDetails.details([
        JSON.stringify(currentSidebarDetailsDataParams),
      ]),
    );

    if (!isOwnerOperator) {
      // clear cache of calendar endpoint under the updated location group and it's individual locations
      queryClient.invalidateQueries(
        queryKeys.getCalendarEvents.details([locationGroupId]),
      );
    } else {
      // clear cache of calendar endpoint under the updated individual OO location
      queryClient.invalidateQueries(
        queryKeys.getCalendarEvents.invalidateAll(),
      );
    }

    if (currentSidebarDetailsDataParams.position === 'all') {
      // clear cache of scheduleDetails endpoint when selecting individual position
      queryClient.invalidateQueries(
        queryKeys.getCalendarSidebarDetails.details([
          JSON.stringify({
            ...currentSidebarDetailsDataParams,
            position: String(currentPosition),
          }),
        ]),
      );
    } else {
      // clear cache of scheduleDetails endpoint when selecting all
      queryClient.invalidateQueries(
        queryKeys.getCalendarSidebarDetails.details([
          JSON.stringify({
            ...currentSidebarDetailsDataParams,
            position: 'all',
          }),
        ]),
      );
    }
  }
};

export const useDeleteScheduleItemOverrides = () => {
  return useMutation({
    mutationFn: deleteScheduleItemOverrides,
    onSuccess: (_, variables) => {
      const {
        currentSidebarDetailsDataParams,
        currentPosition,
        locationGroupId,
      } = variables;
      invalidateCalendarSidebarData(
        currentSidebarDetailsDataParams,
        currentPosition,
        locationGroupId,
        true,
      );
      queryClient.invalidateQueries(queryKeys.scheduleOverview.invalidateAll());
      queryClient.invalidateQueries(queryKeys.schedules.invalidateAll());
      queryClient.invalidateQueries(queryKeys.schedulePreview.invalidateAll());
    },
  });
};

const deleteScheduleItemOverrides = async (payload: {
  scheduleId?: number;
  scheduleItemId?: number;
  narrative?: string;
  currentSidebarDetailsDataParams?: ScheduleDetailsParams;
  currentPosition: number;
  locationGroupId?: string;
}) => {
  const {
    scheduleId,
    scheduleItemId,
    currentSidebarDetailsDataParams,
    narrative,
  } = payload;
  const params = new URLSearchParams({
    ...(scheduleId
      ? {
          schedule_id: String(scheduleId),
        }
      : {}),
    ...(scheduleItemId
      ? {
          schedule_item_id: String(scheduleItemId),
        }
      : {}),
    ...(currentSidebarDetailsDataParams?.locationCode
      ? {
          location_code: currentSidebarDetailsDataParams.locationCode,
        }
      : {}),
    ...(narrative
      ? {
          narrative,
        }
      : {}),
  });
  const url = `/scheduleItemOverrides?${params}`;
  const response = await requestCCApi({
    url,
    method: 'DELETE',
  });

  return response;
};
